/* @jsx h */
import { calculateCheckoutTotal, isPaidByTableCredit } from "@lemmonhq/orders";
import type { CheckoutData, SessionPaymentIntegration } from "@lemmonhq/types";
import * as Sentry from "@sentry/vue";
import { defineComponent, h, computed } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { CheckoutTipComponent } from "./CheckoutTipComponent";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useNativeBridge } from "@app/vue/providers/NativeBridge";
import { instrumentation } from "@app/data/index";
import { useAuth, useCheckout, useToast } from "../../providers";
import { ref } from "vue";
import type { CheckoutActionsProps } from "./CheckoutActions";
import {
  findCheckoutIntegration,
  isPaymentAttemptCurrent,
} from "./paymentIntegrationConfigs";
import { TipsIntegratedPaymentModal } from "./TipsIntegratedPaymentModal";
import {
  endPaymentTrace,
  setPaymentTraceContext,
  setPaymentTraceId,
  startPaymentSpan,
} from "./paymentTrace";

// Bounded so a request that never settles (this flow has been seen to hang for
// 20s+ while the WebView is backgrounded) cannot stay in flight indefinitely.
const ATTACH_REQUEST_TIMEOUT_MS = 8000;

type CheckoutActionsV2Props = CheckoutActionsProps & {
  checkoutId: string;
  /** Pays a checkout table credit fully covers, instead of the terminal. */
  onPayFromCredit?: () => void;
};

export const CheckoutActionsV2 = defineComponent<CheckoutActionsV2Props>({
  name: "CheckoutActionsV2",
  props: [
    "showDiscountModal",
    "showProductDiscountEditorSheet",
    "showCashModal",
    "showCardModal",
    "showTerminalModal",
    "showManualModal",
    "showOnlineModal",
    "showPromotionsModal",
    "showGiftCardModal",
    "checkoutId",
    "onPayFromCredit",
  ],
  setup(props) {
    const checkout = useCheckout();
    const auth = useAuth();
    const toast = useToast();
    const trpc = useTrpc();
    const bridge = useNativeBridge();
    const { t } = useLocalization();
    const showIntegratedTipsModal = ref(false);
    const selectedIntegratedPaymentMethod = ref<"card" | "twint" | null>(null);
    const featureFlags = auth.user.value?.restaurant.feature_flags || {};
    const useIntegratedPaymentTips =
      featureFlags.payment_integration_v2_tips || false;

    const total = computed(() => {
      return checkout.checkout.value
        ? calculateCheckoutTotal(checkout.checkout.value)
        : 0;
    });

    const matchedIntegration = computed(() =>
      findCheckoutIntegration(auth.user.value, trpc, bridge),
    );

    // Hand the provider response to the server. Best-effort: the card is
    // already charged by the time this runs, and the server has an independent
    // confirmation path (the Payrexx webhook cached in temp_caches), so a
    // failure here must NOT fail the payment. Throwing would clear the workflow
    // id, stop the poll loop, and show "Failed to create payment" for a card
    // that was charged -- inviting a second charge. Let the workflow decide.
    const attachProviderResponse = async (input: {
      paymentId: number;
      providerId: string;
      response: unknown;
      status?: "sent" | "confirmed";
      spanAttributes: Record<string, string>;
    }): Promise<void> => {
      const { spanAttributes, ...mutation } = input;
      const controller = new AbortController();
      const timer = setTimeout(
        () => controller.abort("attach-timeout"),
        ATTACH_REQUEST_TIMEOUT_MS,
      );

      try {
        await startPaymentSpan(
          props.checkoutId,
          {
            name: "checkout.integrated_payment.attach_provider_response",
            op: "checkout.payment",
            attributes: {
              ...spanAttributes,
              payment_id: mutation.paymentId,
              provider_status: mutation.status ?? "unknown",
            },
          },
          () =>
            trpc.integratedPaymentWorkflow.attachProviderResponse.mutate(
              mutation,
              { signal: controller.signal },
            ),
        );
      } catch (error: any) {
        console.error("Failed to attach provider response:", error);
        Sentry.captureException(error, {
          level: "error",
          tags: { payment_stage: "attach_provider_response" },
          extra: {
            checkout_id: props.checkoutId,
            payment_id: mutation.paymentId,
            provider_id: mutation.providerId,
          },
        });
        instrumentation.track("checkout_integrated_payment_attach_failed", {
          checkout_id: props.checkoutId,
          payment_id: mutation.paymentId,
          provider_id: mutation.providerId,
          error_message: error?.message,
        });
      } finally {
        clearTimeout(timer);
      }
    };

    const startIntegrationPayment = async (
      paymentMethod: "card" | "twint",
      tipAmount?: number,
    ) => {
      const active = matchedIntegration.value;
      const currentCheckout = checkout.checkout.value;
      if (!active || !currentCheckout) {
        return false;
      }

      const nextTip =
        tipAmount === undefined
          ? currentCheckout.tip
          : tipAmount > 0
            ? { value: tipAmount }
            : undefined;

      const checkoutWithTip: CheckoutData = {
        ...currentCheckout,
        ...(nextTip ? { tip: nextTip } : { tip: undefined }),
      };

      const totalAmount = calculateCheckoutTotal(checkoutWithTip);

      const nextCheckout: CheckoutData = {
        ...checkoutWithTip,
        payment: {
          amountRcvd: totalAmount,
          paymentType: paymentMethod,
        },
        paymentId: undefined,
        paymentIntegrationId: active.integration.id,
        paymentIntegrationWorkflowId: undefined,
        txLoading: true,
        txSuccess: undefined,
      };
      checkout.setCheckout(nextCheckout);

      // Set once the payment row exists; identifies this attempt in the catch.
      let attemptPaymentId: number | undefined;

      try {
        // Start a NEW Sentry trace for the payment so it becomes its OWN root
        // transaction instead of being nested under the page's `/app/pos`
        // navigation trace (mixed in with unrelated catalog/session/report
        // calls). Inside it, a `checkout.integrated_payment` root transaction
        // names the payment.
        //
        // This root deliberately ends with `create`. Every later leg -- the
        // provider sale, the attach, the status polls -- REJOINS the trace as
        // its own segment via `startPaymentSpan` rather than hanging off this
        // root as a child, because a child that outlives its root is discarded
        // without a trace. See paymentTrace.ts. The payment's real duration is
        // reported by the `settled` span from `endPaymentTrace`.
        const spanAttributes = {
          checkout_id: props.checkoutId,
          integration_type: active.integrationType,
          payment_method: paymentMethod,
        };

        const workflowData = await Sentry.startNewTrace(() =>
          Sentry.startSpan(
            {
              name: "checkout.integrated_payment",
              op: "checkout.payment",
              forceTransaction: true,
              attributes: spanAttributes,
            },
            () => {
              // Capture the payment root's trace context so every later leg can
              // rejoin this same trace.
              const traceData = Sentry.getTraceData();
              setPaymentTraceContext(props.checkoutId, {
                sentryTrace: traceData["sentry-trace"],
                baggage: traceData.baggage,
              });

              // Step 1: Create workflow (creates the payment record and kicks
              // off the workflow). This create span propagates the trace to the
              // server and on into the DBOS workflow.
              return Sentry.startSpan(
                {
                  name: "checkout.integrated_payment.create",
                  op: "checkout.payment",
                  attributes: spanAttributes,
                },
                () =>
                  trpc.integratedPaymentWorkflow.create.mutate({
                    integrationId: active.integration.id,
                    integrationType: active.integrationType,
                    checkoutId: props.checkoutId,
                    checkout: nextCheckout,
                    paymentMethod,
                  }),
              );
            },
          ),
        );
        // The payment row now exists, so every span from here on -- and the
        // `settled` span built after the payment is over -- can carry its id.
        setPaymentTraceId(props.checkoutId, workflowData.paymentId);
        attemptPaymentId = workflowData.paymentId;

        // Step 4: Update checkout state with paymentId and workflowId
        checkout.setCheckout({
          ...nextCheckout,
          paymentId: workflowData.paymentId,
          paymentIntegrationWorkflowId: workflowData.workflowId,
          paymentIntegrationWorkflowPlatform: workflowData.workflowPlatform,
          paymentIntegrationWorkflowStatus: {
            status: "initializing",
            payment: workflowData.payment,
          },
        });

        instrumentation.track("checkout_integrated_payment_initiated", {
          checkout_id: props.checkoutId,
          payment_id: workflowData.paymentId,
          payment_workflow_id: workflowData.workflowId,
          payment_integration_id: active.integration.id,
          payment_integration_type: active.integrationType,
          payment_method: paymentMethod,
          payment_amount: totalAmount,
        });

        // Step 2 & 3: For frontend integrations (device payments), call createPaymentTransaction
        // For backend integrations (terminal payments), the workflow handles everything
        if (!active.config.integration) {
          // Frontend integration (e.g., Payrexx SoftPOS)
          // Call the device to create transaction. For SoftPOS this hands the
          // screen to a native activity for the whole card interaction, so the
          // span is usually the longest thing in the trace -- and while it is
          // open the WebView is backgrounded and its status polls do not reach
          // the server. Tracing it explicitly makes that gap self-evident.
          const response = await startPaymentSpan(
            props.checkoutId,
            {
              name: "checkout.integrated_payment.provider_sale",
              op: "checkout.payment",
              attributes: {
                ...spanAttributes,
                payment_id: workflowData.paymentId,
              },
            },
            () =>
              active.config.createPaymentTransaction({
                paymentId: workflowData.paymentId,
                checkout: nextCheckout,
                paymentMethod,
              }),
          );

          // The card has now been charged; the attach is best-effort from here.
          await attachProviderResponse({
            paymentId: workflowData.paymentId,
            providerId: response.providerId,
            response: response.response,
            status: response.status,
            spanAttributes,
          });
        }
        return true;
        // For backend integrations (e.g., Nakka), the workflow will:
        // 1. Call integration.initiate() to create transaction with gateway
        // 2. Poll integration.checkStatus() for completion
        // Frontend just waits for workflow to complete
      } catch (error: any) {
        console.error("Payment integration error:", error);
        // The device sale can reject long after this attempt ended: when the
        // app loses its reply, the bridge request only times out ~16 minutes
        // later, by which time the webhook has usually completed the sale and
        // the cashier has moved on. Restoring `nextCheckout` then would reopen
        // a paid checkout over whatever is on screen, so only record it.
        const isCurrentAttempt = isPaymentAttemptCurrent(
          checkout.checkout.value,
          attemptPaymentId,
        );
        instrumentation.track("checkout_integrated_payment_initiate_failed", {
          checkout_id: props.checkoutId,
          payment_id: attemptPaymentId,
          payment_integration_id: active.integration.id,
          payment_integration_type: active.integrationType,
          payment_method: paymentMethod,
          payment_amount: totalAmount,
          error_message: error?.message,
          is_current_attempt: isCurrentAttempt,
        });
        if (!isCurrentAttempt) {
          return false;
        }
        // The payment never got off the ground (create failed, or the device
        // sale itself threw before any card was charged), so no poll loop will
        // run to close out the payment's trace.
        endPaymentTrace(props.checkoutId, "failed");
        checkout.setCheckout({
          ...nextCheckout,
          paymentId: undefined,
          paymentIntegrationId: undefined,
          paymentIntegrationWorkflowId: undefined,
          paymentIntegrationWorkflowStatus: undefined,
          txLoading: false,
        });
        toast.showToast(error?.message || "Failed to create payment", {
          type: "error",
        });
        return false;
      }
    };

    const openIntegratedPaymentFlow = (paymentMethod: "card" | "twint") => {
      const cd = checkout.checkout.value;
      if (cd && isPaidByTableCredit(cd) && props.onPayFromCredit) {
        props.onPayFromCredit();
        return;
      }
      // Only SoftPOS (tap to pay on the phone) asks for a tip in the POS, and
      // only with payment_integration_v2_tips on: payment terminals take the
      // tip on the device.
      if (
        !useIntegratedPaymentTips ||
        matchedIntegration.value?.integrationType !== "payrexx_softpos"
      ) {
        startIntegrationPayment(paymentMethod);
        return;
      }

      selectedIntegratedPaymentMethod.value = paymentMethod;
      showIntegratedTipsModal.value = true;
    };

    const closeIntegratedTipsModal = () => {
      showIntegratedTipsModal.value = false;
      selectedIntegratedPaymentMethod.value = null;
    };

    const handleIntegratedTipsConfirm = async (tipAmount: number) => {
      const paymentMethod = selectedIntegratedPaymentMethod.value;

      if (!paymentMethod) {
        return;
      }

      const started = await startIntegrationPayment(paymentMethod, tipAmount);

      if (started) {
        closeIntegratedTipsModal();
      }
    };

    return () => {
      const checkoutData = checkout.checkout.value;
      if (!checkoutData) {
        return null;
      }

      const {
        showCashModal,
        showDiscountModal,
        showProductDiscountEditorSheet,
        showManualModal,
        showCardModal,
      } = props;
      const hasOnlinePayrexx =
        !!auth.user.value?.restaurant.payrexx_name &&
        auth.user.value?.restaurant.payrexx_name !== "empty";
      const active = matchedIntegration.value;
      const cardPaymentDisabled = total.value <= 0;
      const canUseProductDiscountEditor =
        checkoutData.payload?.type === "cart" ||
        (checkoutData.payload?.type === "table" &&
          (checkoutData.payload.object.type === "full" ||
            checkoutData.payload.object.type === "partial"));

      return (
        <div>
          <div class="mx-auto grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 sm:w-[700px]">
            <CheckoutTipComponent />
            <div
              class={`grid gap-2 ${canUseProductDiscountEditor ? "grid-cols-2" : "grid-cols-1"}`}
            >
              <button
                class="sm:w-full btn sm:btn-lg btn-secondary text-1xl lg:text-1xl font-semibold"
                onClick={() => (showDiscountModal.value = true)}
              >
                <span class="material-symbols-outlined mr-1 text-1xl lg:text-1xl">
                  percent
                </span>
                {t("pos.checkout.order_discount_button")}
              </button>
              {canUseProductDiscountEditor ? (
                <button
                  class="sm:w-full btn sm:btn-lg btn-secondary text-1xl lg:text-1xl font-semibold"
                  onClick={() => (showProductDiscountEditorSheet.value = true)}
                >
                  <span class="material-symbols-outlined mr-1 text-1xl lg:text-1xl">
                    local_offer
                  </span>
                  {t("pos.checkout.items_discount_button")}
                </button>
              ) : null}
            </div>
            <button
              class="sm:w-full sm:btn-lg btn btn-accent text-1xl lg:text-1xl font-semibold"
              onClick={() => (props.showPromotionsModal.value = true)}
            >
              <span class="flex justify-items items-center">
                <span class="material-symbols-outlined mr-1 text-1xl lg:text-1xl">
                  social_leaderboard
                </span>{" "}
                Rewards
                {checkoutData.promotions && checkoutData.promotions.length > 0
                  ? `[${checkoutData.promotions.length}]`
                  : ""}
              </span>
            </button>
            <button
              disabled={!hasOnlinePayrexx || total.value < 0.5}
              class="sm:w-full sm:btn-lg btn btn-neutral text-1xl lg:text-1xl font-semibold"
              onClick={() => (props.showOnlineModal.value = true)}
            >
              <span class="flex justify-items items-center">
                <span class="material-symbols-outlined mr-1 text-1xl lg:text-1xl">
                  qr_code_2
                </span>{" "}
                QR Pay
              </span>
            </button>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-4">
              <button
                class="sm:w-full sm:btn-lg btn btn-neutral text-1xl lg:text-1xl font-semibold"
                onClick={() => (showCashModal.value = true)}
              >
                <span class="flex justify-items items-center">
                  <span class="material-symbols-outlined mr-1 text-1xl lg:text-1xl">
                    payments
                  </span>{" "}
                  {t("pos.checkout.cash_button")}
                </span>
              </button>
              <button
                class="sm:w-full sm:btn-lg btn btn-neutral text-1xl lg:text-1xl font-semibold"
                onClick={() => {
                  showManualModal.value = true;
                }}
              >
                <span class="flex justify-items items-center">
                  <span class="material-symbols-outlined mr-1 text-1xl lg:text-2xl">
                    edit_square
                  </span>{" "}
                  {t("pos.checkout.manual_button")}
                </span>
              </button>
            </div>
            {active ? (
              active.config.buttonMode === "single-card" ? (
                <button
                  disabled={cardPaymentDisabled}
                  onClick={() => openIntegratedPaymentFlow("card")}
                  class="sm:w-full sm:btn-lg btn btn-primary text-1xl lg:text-1xl font-semibold"
                >
                  <span class="flex justify-items items-center">
                    <span class="material-symbols-outlined mr-1 text-1xl lg:text-2xl">
                      credit_card
                    </span>{" "}
                    {t("pos.checkout.card_button")}
                  </span>
                </button>
              ) : (
                <div class="flex flex-col">
                  <div class="grid grid-cols-2 gap-4 lg:gap-4">
                    <button
                      disabled={cardPaymentDisabled}
                      onClick={() => openIntegratedPaymentFlow("card")}
                      class="sm:btn-lg btn btn-primary text-xl font-semibold"
                    >
                      <span class="flex justify-items items-center">
                        <span class="material-symbols-outlined mr-1 text-xl">
                          credit_card
                        </span>{" "}
                        {t("pos.checkout.card_button")}
                      </span>
                    </button>
                    <button
                      disabled={cardPaymentDisabled}
                      onClick={() => openIntegratedPaymentFlow("twint")}
                      class="sm:btn-lg btn btn-primary text-xl font-semibold"
                    >
                      <span class="flex justify-items items-center">
                        <span class="material-symbols-outlined mr-1 text-xl">
                          hexagon
                        </span>{" "}
                        {t("pos.checkout.twint_button")}
                      </span>
                    </button>
                  </div>
                </div>
              )
            ) : (
              <button
                disabled={cardPaymentDisabled}
                onClick={() => (showCardModal.value = true)}
                class="sm:w-full sm:btn-lg btn btn-primary text-1xl lg:text-1xl font-semibold"
              >
                <span class="flex justify-items items-center">
                  <span class="material-symbols-outlined mr-1 text-1xl lg:text-2xl">
                    credit_card
                  </span>{" "}
                  {t("pos.checkout.card_button")}
                </span>
              </button>
            )}

            <TipsIntegratedPaymentModal
              isOpen={showIntegratedTipsModal.value}
              paymentMethod={selectedIntegratedPaymentMethod.value}
              totalAmount={total.value}
              initialTipAmount={checkoutData.tip?.value}
              onClose={closeIntegratedTipsModal}
              onConfirm={handleIntegratedTipsConfirm}
            />
          </div>
        </div>
      );
    };
  },
});
